import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { financialKeys, DashboardData, GetDashboardDataParams } from './api/useFinancialData';

// Local storage keys
const CACHE_KEYS = {
  DASHBOARD_PREFERENCES: 'financial_dashboard_preferences',
  LAST_VIEWED_DATA: 'financial_last_viewed_data',
  CHART_SETTINGS: 'financial_chart_settings',
} as const;

// Cache configuration
const CACHE_CONFIG = {
  STALE_TIME: Infinity, // Never consider data stale
  CACHE_TIME: 30 * 60 * 1000, // 30 minutes
  MAX_CACHED_QUERIES: 10,
} as const;

// Cache version - increment when changing default behavior
const CACHE_VERSION = 2;

// Types for cached data
interface CachedDashboardPreferences {
  version?: number;
  selectedProperties: string[];
  dateRange: {
    startDate: string;
    endDate: string;
    preset?: string;
  };
  chartSettings: {
    revenueChartType: 'line' | 'bar' | 'area';
    expenseChartType: 'pie' | 'doughnut' | 'bar';
    showProjections: boolean;
  };
  lastUpdated: number;
}

interface CachedDashboardData {
  params: GetDashboardDataParams;
  data: DashboardData;
  timestamp: number;
}

export const useFinancialDataCache = () => {
  const queryClient = useQueryClient();

  // Save dashboard preferences to localStorage
  const saveDashboardPreferences = useCallback((preferences: Partial<CachedDashboardPreferences>) => {
    try {
      const existing = localStorage.getItem(CACHE_KEYS.DASHBOARD_PREFERENCES);
      const current = existing ? JSON.parse(existing) : {};
      
      const updated = {
        ...current,
        ...preferences,
        version: CACHE_VERSION,
        lastUpdated: Date.now(),
      };
      
      localStorage.setItem(CACHE_KEYS.DASHBOARD_PREFERENCES, JSON.stringify(updated));
    } catch (error) {
      console.warn('Failed to save dashboard preferences:', error);
    }
  }, []);

  // Load dashboard preferences from localStorage
  const loadDashboardPreferences = useCallback((): CachedDashboardPreferences | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEYS.DASHBOARD_PREFERENCES);
      if (!cached) return null;
      
      const preferences = JSON.parse(cached);
      
      // Check cache version - invalidate if outdated
      if (!preferences.version || preferences.version < CACHE_VERSION) {
        localStorage.removeItem(CACHE_KEYS.DASHBOARD_PREFERENCES);
        return null;
      }
      
      // Check if preferences are not too old (7 days)
      const maxAge = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - preferences.lastUpdated > maxAge) {
        localStorage.removeItem(CACHE_KEYS.DASHBOARD_PREFERENCES);
        return null;
      }
      
      return preferences;
    } catch (error) {
      console.warn('Failed to load dashboard preferences:', error);
      return null;
    }
  }, []);

  // Cache dashboard data in localStorage for offline access
  const cacheLastViewedData = useCallback((params: GetDashboardDataParams, data: DashboardData) => {
    try {
      const cachedData: CachedDashboardData = {
        params,
        data,
        timestamp: Date.now(),
      };
      
      localStorage.setItem(CACHE_KEYS.LAST_VIEWED_DATA, JSON.stringify(cachedData));
    } catch (error) {
      console.warn('Failed to cache dashboard data:', error);
    }
  }, []);

  // Load cached dashboard data for offline access
  const loadLastViewedData = useCallback((): CachedDashboardData | null => {
    try {
      const cached = localStorage.getItem(CACHE_KEYS.LAST_VIEWED_DATA);
      if (!cached) return null;
      
      const data = JSON.parse(cached);
      
      // Check if data is not too old (1 hour)
      const maxAge = 60 * 60 * 1000;
      if (Date.now() - data.timestamp > maxAge) {
        localStorage.removeItem(CACHE_KEYS.LAST_VIEWED_DATA);
        return null;
      }
      
      return data;
    } catch (error) {
      console.warn('Failed to load cached dashboard data:', error);
      return null;
    }
  }, []);



  // Clean up old cache entries
  const cleanupCache = useCallback(() => {
    try {
      const cache = queryClient.getQueryCache();
      const queries = cache.getAll();
      
      // Remove old financial queries beyond the limit
      const financialQueries = queries
        .filter(query => query.queryKey[0] === 'financial')
        .sort((a, b) => (b.state.dataUpdatedAt || 0) - (a.state.dataUpdatedAt || 0));
      
      if (financialQueries.length > CACHE_CONFIG.MAX_CACHED_QUERIES) {
        const toRemove = financialQueries.slice(CACHE_CONFIG.MAX_CACHED_QUERIES);
        toRemove.forEach(query => {
          queryClient.removeQueries({ queryKey: query.queryKey });
        });
      }
    } catch (error) {
      console.warn('Failed to cleanup cache:', error);
    }
  }, [queryClient]);

  // Initialize cache optimizations
  useEffect(() => {
    // Cleanup old cache entries on mount
    cleanupCache();
    
    // Set up periodic cleanup (only for memory management)
    const cleanupInterval = setInterval(cleanupCache, 30 * 60 * 1000); // Every 30 minutes
    
    return () => {
      clearInterval(cleanupInterval);
    };
  }, [cleanupCache]);

  // Clear all cached preferences (useful for debugging or major updates)
  const clearAllCache = useCallback(() => {
    try {
      localStorage.removeItem(CACHE_KEYS.DASHBOARD_PREFERENCES);
      localStorage.removeItem(CACHE_KEYS.LAST_VIEWED_DATA);
      localStorage.removeItem(CACHE_KEYS.CHART_SETTINGS);
      queryClient.clear();
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }, [queryClient]);

  return {
    saveDashboardPreferences,
    loadDashboardPreferences,
    cacheLastViewedData,
    loadLastViewedData,
    cleanupCache,
    clearAllCache,
  };
};

// Hook for optimized query configuration
export const useOptimizedQueryConfig = () => {
  return {
    staleTime: CACHE_CONFIG.STALE_TIME,
    cacheTime: CACHE_CONFIG.CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false, // No retries - show error immediately
    retryDelay: 0,
  };
};